"use client";

import {
  Children,
  type ReactElement,
  type ReactNode,
  cloneElement,
  type FC,
  type Ref,
} from "react";
import { motion } from "motion/react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { mergeClasses } from "@huub-web/utils";

import type { ChildrenProp, ClassNameProp } from "../../props";
import { useUuid } from "../../hooks/useUuid";
import {
  TargetPositioner,
  type TargetPositionerProps,
} from "../TargetPositioner/TargetPositioner";
import {
  positionerTransition,
  positionerVariants,
} from "../TargetPositioner/TargetPositioner.motion";
import { mergeRefs } from "../../utils/mergeRefs";
import { zIndexes } from "../../theme/zIndex";

import { typography } from "../../theme/css/typography.css";
import { positionerStyle, tooltipStyle } from "./Tooltip.css";

export type TooltipProps = ChildrenProp &
  ClassNameProp &
  Pick<TargetPositionerProps, "placement" | "targetRef" | "offset"> &
  RecipeVariants<typeof tooltipStyle> & {
    id?: string;
    value?: ReactNode;
    focus?: boolean;
    hover?: boolean;
    click?: boolean;
    active?: boolean;
    disabled?: boolean;
    /**
     * Ref forwarded to the tooltip element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * Tooltip component that displays a message when hovering over a target element.
 */
export const Tooltip: FC<TooltipProps> = ({
  id,
  className,
  value,
  focus = false,
  disabled = false,
  hover = true,
  click = false,
  placement = "top",
  variant = "default",
  active,
  targetRef,
  offset,
  children,
  ref,
  ...props
}) => {
  const uuid = useUuid(id);

  const childrenArray = Children.toArray(children);
  const firstChild = childrenArray?.[0] as ReactElement<any>;

  if (typeof window === "undefined") {
    return (
      <>
        {firstChild &&
          cloneElement(firstChild, {
            ref: mergeRefs(targetRef),
            "aria-describedby": id,
            ...(active ? { active } : {}),
          })}
        {childrenArray.slice(1)}
      </>
    );
  }

  return (
    <TargetPositioner
      placement={placement}
      targetRef={targetRef}
      active={active}
      className={positionerStyle}
      content={
        value && !disabled ? (
          <motion.div
            ref={ref}
            id={uuid}
            role="tooltip"
            className={mergeClasses(
              tooltipStyle({ variant }),
              typography.bodyS,
              className,
            )}
            transition={positionerTransition}
            variants={positionerVariants()[placement]}
            initial="initial"
            animate="animate"
            exit="exit"
            custom={location}
            {...props}
          >
            {value}
          </motion.div>
        ) : undefined
      }
      focus={focus}
      hover={hover}
      click={click}
      zIndex={zIndexes.tooltip}
      offset={offset}
    >
      {children}
    </TargetPositioner>
  );
};
